(function () {
  if (customElements.get('ce-dialog') !== undefined) {
    return;
  }

  class Dialog extends HTMLParsedElement {
    constructor() {
      super();

      this.queuedAttributeChangedCallbackParams = [];

      this.selectors = {
        details: '[data-dialog-details]',
        buttonToggle: '[data-dialog-button-toggle]',
        buttonOpen: '[data-dialog-button-open]',
        buttonClose: '[data-dialog-button-close]',
        panel: '[data-dialog-panel]',
        backdrop: '[data-backdrop-90]',
      };

      if (this.hasAttribute('backdrop')) {
        this.selectors.backdrop = this.getAttribute('backdrop');
      }

      this.elements = null;

      this.animations = { open: [], close: [] };
      this.elementToFocus = null;
      this.position = this.getAttribute('position');
      this.type = this.getAttribute('type');

      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleClickButton = this.handleClickButton.bind(this);
      this.handleClickClose = this.handleClickClose.bind(this);
    }

    get isOpen() {
      return this.getAttribute('is-open') === 'true';
    }

    set isOpen(value) {
      this.setAttribute('is-open', value);
    }

    static get observedAttributes() {
      return ['is-open'];
    }

    attributeChangedCallback(...params) {
      if (!this.parsed) {
        this.queuedAttributeChangedCallbackParams.push(params);
        return;
      }

      const [name, oldValue, newValue] = params;

      if (oldValue === newValue) {
        return;
      }

      switch (name) {
        case 'is-open':
          if (newValue !== null) {
            if (newValue === 'true') {
              this.elementToFocus = document.activeElement;

              if (!Shopify.designMode) {
                trapFocus(this.elementToFocus.hasAttribute('data-dialog-button-toggle') ? this : this.elements.panel, { elementToFocus: this.elements.buttonsClose[0] || this.elements.buttonsToggle[0] });
              }

              for (let buttonToggle of this.elements.buttonsToggle) {
                buttonToggle.setAttribute('aria-expanded', 'true');
                buttonToggle.setAttribute('aria-pressed', 'true');
              }

              for (let buttonOpen of this.elements.buttonsOpen) {
                buttonOpen.setAttribute('aria-expanded', 'true');
              }

              for (let buttonClose of this.elements.buttonsClose) {
                buttonClose.setAttribute('aria-expanded', 'true');
              }

              this.dispatchEvent(new CustomEvent('ce-dialog:is-open', { detail: true }));
            } else {
              if (!Shopify.designMode) {
                removeTrapFocus();
              }

              this.elementToFocus?.focus({ preventScroll: true });

              for (let buttonToggle of this.elements.buttonsToggle) {
                buttonToggle.setAttribute('aria-expanded', 'false');
                buttonToggle.setAttribute('aria-pressed', 'false');
              }

              for (let buttonOpen of this.elements.buttonsOpen) {
                buttonOpen.setAttribute('aria-expanded', 'false');
              }

              for (let buttonClose of this.elements.buttonsClose) {
                buttonClose.setAttribute('aria-expanded', 'false');
              }

              this.elements.details.removeAttribute('open');

              this.dispatchEvent(new CustomEvent('ce-dialog:is-open', { detail: false }));
            }
          }

          break;
      }
    }

    parsedCallback() {
      this.elements = {
        details: this.querySelector(this.selectors.details),
        buttonsToggle: this.querySelectorAll(this.selectors.buttonToggle),
        buttonsOpen: this.querySelectorAll(this.selectors.buttonOpen),
        buttonsClose: this.querySelectorAll(this.selectors.buttonClose),
        panel: this.querySelector(this.selectors.panel),
        backdrop: document.querySelector(this.selectors.backdrop),
      };

      this.createAnimationOpenBackdrop();
      this.createAnimationOpenDialog();
      this.createAnimationCloseDialog();
      this.createAnimationCloseBackdrop();

      this.addEventListener('keydown', this.handleKeyDown);

      for (let buttonToggle of this.elements.buttonsToggle) {
        buttonToggle.addEventListener('click', this.handleClickButton);
      }

      for (let buttonOpen of this.elements.buttonsOpen) {
        buttonOpen.addEventListener('click', this.handleClickButton);
      }

      for (let buttonClose of this.elements.buttonsClose) {
        buttonClose.addEventListener('click', this.handleClickClose);
        buttonClose.addEventListener('touchend', this.handleClickClose, { passive: false });
      }

      for (let params of this.queuedAttributeChangedCallbackParams) {
        this.attributeChangedCallback(...params);
      }
    }

    disconnectedCallback() {
      if (Shopify.designMode) {
        this.elements.backdrop.style.opacity = 0;
        this.elements.backdrop.style.pointerEvents = 'none';
      }

      this.removeEventListener('keydown', this.handleKeyDown);

      for (let buttonToggle of this.elements.buttonsToggle) {
        buttonToggle.removeEventListener('click', this.handleClickButton);
      }

      for (let buttonOpen of this.elements.buttonsOpen) {
        buttonOpen.removeEventListener('click', this.handleClickButton);
      }

      for (let buttonClose of this.elements.buttonsClose) {
        buttonClose.removeEventListener('click', this.handleClickClose);
        buttonClose.removeEventListener('touchend', this.handleClickClose);
      }
    }

    handleKeyDown(event) {
      if (!this.isOpen || event.key !== 'Escape') {
        return;
      }

      if (!this.isOpen) {
        if (this.isAnimationOpenRunning()) {
          this.reverseAnimationsOpen();
        } else {
          this.elements.details.setAttribute('open', '');
          this.playAnimationsOpen();
        }
      } else {
        if (this.isAnimationCloseRunning()) {
          this.reverseAnimationsClose();
        } else {
          this.playAnimationsClose();
        }
      }
    }

    handleClickButton(event) {
      event.preventDefault();

      if (!this.isOpen) {
        if (this.isAnimationOpenRunning()) {
          this.reverseAnimationsOpen();
        } else {
          this.elements.details.setAttribute('open', '');
          trapBodyScroll();
          this.playAnimationsOpen();
        }
      } else {
        if (this.isAnimationCloseRunning()) {
          trapBodyScroll();
          this.reverseAnimationsClose();
        } else {
          this.playAnimationsClose();
        }
      }
    }

    isMenuVisible() {
      return this.isOpen || this.elements.details.hasAttribute('open');
    }

    handleClickClose(event) {
      const now = Date.now();

      if (this._lastCloseAttempt && now - this._lastCloseAttempt < 400) {
        return;
      }

      this._lastCloseAttempt = now;

      if (event.type === 'touchend') {
        event.preventDefault();
      }

      event.stopPropagation();

      if (!this.isMenuVisible()) {
        return;
      }

      if (this.isAnimationCloseRunning()) {
        trapBodyScroll();
        this.reverseAnimationsClose();
        return;
      }

      if (this.isOpen) {
        this.playAnimationsClose();
        return;
      }

      this.closeMenuImmediately();
    }

    closeMenuImmediately() {
      removeTrapBodyScroll();
      removeTrapFocus();
      this.elements.details.removeAttribute('open');

      if (this.elements.backdrop) {
        this.elements.backdrop.style.opacity = '0';
        this.elements.backdrop.style.pointerEvents = 'none';
      }

      this.isOpen = false;
    }

    createAnimationOpenBackdrop() {
      let keyframes = [];

      if (Shopify.designMode) {
        keyframes = [
          { opacity: 0, pointerEvents: 'none', position: 'absolute' },
          { opacity: 1, pointerEvents: 'auto', position: 'fixed' },
        ];
      } else {
        keyframes = [
          { opacity: 0, pointerEvents: 'none' },
          { opacity: 1, pointerEvents: 'auto' },
        ];
      }

      const options = {
        delay: 0,
        duration: Themu.prefersReducedMotion ? 0 : 150,
        easing: Themu.animation.easeIn,
        endDelay: Themu.prefersReducedMotion ? 0 : 300,
        fill: 'both',
      };

      const animation = this.elements.backdrop.animate(keyframes, options);
      animation.cancel();

      animation.addEventListener('finish', () => {
        animation.commitStyles();
        animation.cancel();
      });

      this.animations.open.push(animation);
    }

    createAnimationOpenDialog() {
      let keyframes = [];

      if (this.type === 'drawer') {
        if (this.position === 'left') {
          keyframes = [{ transform: 'translateX(calc(-100% - 0.5rem))' }, { transform: 'translateX(0)' }];
        }

        if (this.position === 'right') {
          keyframes = [{ transform: 'translateX(calc(100% + 0.5rem))' }, { transform: 'translateX(0)' }];
        }
      }

      if (this.type === 'modal') {
        keyframes = [
          { opacity: 0, transform: 'translateY(-50%) scale(0.95)' },
          { opacity: 1, offset: 0.5 },
          { opacity: 1, transform: 'translateY(-50%) scale(1)' },
        ];
      }

      if (this.type === 'menu') {
        keyframes = [
          { opacity: 0, transform: 'translateY(calc(100% + 1rem))' },
          { opacity: 1, offset: 0.5 },
          { opacity: 1, transform: 'translateY(0)' },
        ]
      }

      const options = {
        delay: Themu.prefersReducedMotion ? 0 : 150,
        duration: Themu.prefersReducedMotion ? 0 : 300,
        easing: Themu.animation.easeIn,
        endDelay: 0,
        fill: 'both',
      };

      const animation = this.elements.panel.animate(keyframes, options);
      animation.cancel();

      animation.addEventListener('finish', () => {
        const { progress } = animation.effect.getComputedTiming();

        animation.commitStyles();
        animation.cancel();

        this.isOpen = progress === 1;

        if (!this.isOpen) {
          removeTrapBodyScroll();
        }
      });

      this.animations.open.push(animation);
    }

    createAnimationCloseDialog() {
      let keyframes = [];

      if (this.type === 'drawer') {
        if (this.position === 'left') {
          keyframes = [{ transform: 'translateX(0)' }, { transform: 'translateX(calc(-100% - 0.5rem))' }];
        }

        if (this.position === 'right') {
          keyframes = [{ transform: 'translateX(0)' }, { transform: 'translateX(calc(100% + 0.5rem))' }];
        }
      }

      if (this.type === 'modal') {
        keyframes = [
          { opacity: 1, transform: 'translateY(-50%) scale(1)' },
          { opacity: 0, offset: 0.5 },
          { opacity: 0, transform: 'translateY(-50%) scale(0.95)' },
        ];
      }

      if (this.type === 'menu') {
        keyframes = [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, offset: 0.5 },
          { opacity: 0, transform: 'translateY(calc(100% + 1rem))' },
        ]
      }

      const options = {
        delay: 0,
        duration: Themu.prefersReducedMotion ? 0 : 200,
        easing: Themu.animation.easeOut,
        endDelay: Themu.prefersReducedMotion ? 0 : 150,
        fill: 'both',
      };

      const animation = this.elements.panel.animate(keyframes, options);
      animation.cancel();

      animation.addEventListener('finish', () => {
        animation.commitStyles();
        animation.cancel();
      });

      this.animations.close.push(animation);
    }

    createAnimationCloseBackdrop() {
      let keyframes = [];

      if (Shopify.designMode) {
        keyframes = [
          { opacity: 1, pointerEvents: 'auto', position: 'fixed' },
          { opacity: 0, pointerEvents: 'none', position: 'absolute' },
        ];
      } else {
        keyframes = [
          { opacity: 1, pointerEvents: 'auto' },
          { opacity: 0, pointerEvents: 'none' },
        ];
      }

      const options = {
        delay: Themu.prefersReducedMotion ? 0 : 200,
        duration: Themu.prefersReducedMotion ? 0 : 150,
        easing: Themu.animation.easeOut,
        endDelay: 0,
        fill: 'both',
      };

      const animation = this.elements.backdrop.animate(keyframes, options);
      animation.cancel();

      animation.addEventListener('finish', () => {
        const { progress } = animation.effect.getComputedTiming();

        animation.commitStyles();
        animation.cancel();

        this.isOpen = progress !== 1;

        if (!this.isOpen) {
          removeTrapBodyScroll();
        }
      });

      this.animations.close.push(animation);
    }

    isAnimationOpenRunning() {
      for (let animation of this.animations.open) {
        if (animation.playState === 'running') {
          return true;
        }
      }

      return false;
    }

    isAnimationCloseRunning() {
      for (let animation of this.animations.close) {
        if (animation.playState === 'running') {
          return true;
        }
      }

      return false;
    }

    playAnimationsOpen() {
      for (let animation of this.animations.open) {
        animation.updatePlaybackRate(1);
        animation.play();
      }
    }

    playAnimationsClose() {
      for (let animation of this.animations.close) {
        animation.updatePlaybackRate(1);
        animation.play();
      }
    }

    reverseAnimationsOpen() {
      for (let animation of this.animations.open) {
        animation.updatePlaybackRate(animation.playbackRate * -1);
      }
    }

    reverseAnimationsClose() {
      for (let animation of this.animations.close) {
        animation.updatePlaybackRate(animation.playbackRate * -1);
      }
    }

    shopifyDesignModeOpen() {
      if (this.isOpen) {
        return;
      }

      this.elements.details.setAttribute('open', '');
      this.playAnimationsOpen();
    }
  }

  customElements.define('ce-dialog', Dialog);
})();
